<template>
  <iColumn
    v-click-outside="() => $emit('close')"
    :min-width="100"
    class="dynamic-component-contextual-menu"
    height="hug"
    width="hug"
    :theme-id="0"
  >
    <iMenu
      hover-to-open-sub-menu
      :items="dropdownItems"
      orientation="vertical"
      submenu-style="floating"
      :active-item="showSubMenu"
      @click="handleAction"
    />
  </iColumn>
</template>

<script>

import { MenuConstants } from "@vcl";
import { mapActions } from "pinia";
import useDynamicComponentBuilderStore from "%/stores/dynamic-component-builder-store";

export default {
  name: "DynamicComponentContextualMenu",
  props: {
    show: {
      type: Boolean,
      required: false,
      default: false,
    },
    name: {
      type: String,
      required: false,
      default: "",
    },
    canCopy: {
      type: Boolean,
      required: false,
      default: false,
    },
    canPaste: {
      type: Boolean,
      required: false,
      default: false,
    },
    isEditable: {
      type: Boolean,
      required: false,
      default: false,
    },
    isDeletable: {
      type: Boolean,
      required: false,
      default: false,
    },
    isDuplicatable: {
      type: Boolean,
      required: false,
      default: false,
    },
    canWrapInVerticalContainer: {
      type: Boolean,
      required: false,
      default: false,
    },
    canWrapInHorizontalContainer: {
      type: Boolean,
      required: false,
      default: false,
    },
    canWrapSelectionInContainer: {
      type: Boolean,
      required: false,
      default: false,
    },
    canCreateCustomComponent: {
      type: Boolean,
      required: false,
      default: false,
    },
    canDetach: {
      type: Boolean,
      required: false,
      default: false,
    },
    isTemplatedComponent: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: [
    "edit",
    "delete",
    "duplicate",
    "close",
    "wrap-in-vertical-container",
    "wrap-in-horizontal-container",
    "wrap-selection-in-horizontal-container",
    "wrap-selection-in-vertical-container",
    "create-custom-component",
    "copy",
    "paste",
    "detach",
    "create-new-template-from-here",
  ],
  data() {
    return {
      showSubMenu: "",
      clipboardEmpty: true,
      debounce: null,
      dropdownItems: [],
    };
  },
  computed: {
    isFirefox() {
      return navigator.userAgent.toLowerCase().includes("firefox");
    },
    shortcutParentKey() {
      return this.getOS() === "windows" ? "ctrl" : "⌘";
    },
  },
  watch: {
    isEditable: {
      handler() {
        this.buildAndSetDropdownItems();
      },
      immediate: true,
    },
    isDeletable() {
      this.buildAndSetDropdownItems();
    },
    isDuplicatable() {
      this.buildAndSetDropdownItems();
    },
    canWrapInVerticalContainer() {
      this.buildAndSetDropdownItems();
    },
    canWrapInHorizontalContainer() {
      this.buildAndSetDropdownItems();
    },
    canCreateCustomComponent() {
      this.buildAndSetDropdownItems();
    },
  },
  created() {
    this.buildAndSetDropdownItems();
  },
  methods: {
    ...mapActions(useDynamicComponentBuilderStore, [
      "setShowContextualMenuForIdInBuilder",
      "setShowContextualMenuForIdInLayers",
      "getOS",
    ]),

    buildAndSetDropdownItems() {
      this.dropdownItems = this.buildDropdownItems();
    },
    buildDropdownItems() {
      const items = [];

      const editItem = {
        name: "edit",
        label: "Edit",
        actionType: MenuConstants.ACTION_TYPE_BUTTON_CLICK,
        actionValue: "edit",
      };

      const copyItem = {
        name: "copy",
        label: "Copy",
        secondaryLabel: `${this.shortcutParentKey} + C`,
        actionType: MenuConstants.ACTION_TYPE_BUTTON_CLICK,
        actionValue: "copy",
      };

      const pasteItem = {
        name: "paste",
        label: "Paste",
        secondaryLabel: `${this.shortcutParentKey} + V`,
        actionType: MenuConstants.ACTION_TYPE_BUTTON_CLICK,
        actionValue: "paste",
      };

      const duplicateItem = {
        name: "duplicate",
        label: "Duplicate",
        secondaryLabel: `${this.shortcutParentKey} + D`,
        actionType: MenuConstants.ACTION_TYPE_BUTTON_CLICK,
        actionValue: "duplicate",
      };

      const deleteItem = {
        name: "delete",
        label: "Delete",
        secondaryLabel: "⌫",
        actionType: MenuConstants.ACTION_TYPE_BUTTON_CLICK,
        actionValue: "delete",
      };

      const customComponentItem = {
        name: "createCustomComponent",
        label: "Create Template",
        actionType: MenuConstants.ACTION_TYPE_BUTTON_CLICK,
        actionValue: "createCustomComponent",
      };

      if (this.isEditable) {
        items.push(editItem);
      }

      if ((this.canWrapInVerticalContainer || this.canWrapInHorizontalContainer) && !this.canWrapSelectionInContainer) {
        const wrapActions = {
          name: "wrap",
          label: "Wrap",
          actionType: MenuConstants.ACTION_TYPE_BUTTON_CLICK,
          actionValue: "wrap",
          subItems: [],
        };

        if (this.canWrapInVerticalContainer) {
          wrapActions.subItems.push({
            name: "wrapInVerticalContainer",
            label: "Wrap in Vertical Container",
            actionType: MenuConstants.ACTION_TYPE_BUTTON_CLICK,
            actionValue: "wrapInVerticalContainer",
          });
        }

        if (this.canWrapInHorizontalContainer) {
          wrapActions.subItems.push({
            name: "wrapInHorizontalContainer",
            label: "Wrap in Horizontal Container",
            actionType: MenuConstants.ACTION_TYPE_BUTTON_CLICK,
            actionValue: "wrapInHorizontalContainer",
          });
        }

        items.push(wrapActions);
      }

      if (this.canWrapSelectionInContainer) {
        const multiWrapActions = {
          name: "wrapSelection",
          label: "Wrap Selection",
          actionType: MenuConstants.ACTION_TYPE_BUTTON_CLICK,
          actionValue: "wrap",
          subItems: [],
        };

        multiWrapActions.subItems.push({
          name: "wrapSelectionInHorizontalContainer",
          label: "Wrap selection in Horizontal Container",
          actionType: MenuConstants.ACTION_TYPE_BUTTON_CLICK,
          actionValue: "wrapSelectionInHorizontalContainer",
        });

        multiWrapActions.subItems.push({
          name: "wrapSelectionInVerticalContainer",
          label: "Wrap selection in Vertical Container",
          actionType: MenuConstants.ACTION_TYPE_BUTTON_CLICK,
          actionValue: "wrapSelectionInVerticalContainer",
        });

        multiWrapActions.subItems.push({
          name: "wrapSelectionInContainer",
          label: "Wrap selection in Container",
          actionType: MenuConstants.ACTION_TYPE_BUTTON_CLICK,
          actionValue: "wrapSelectionInContainer",
        });

        items.push(multiWrapActions);
      }

      if (this.canDetach) {
        items.push({
          name: "detach",
          label: "Detach",
          actionType: MenuConstants.ACTION_TYPE_BUTTON_CLICK,
          actionValue: "detach",
        });
      }

      if (this.isTemplatedComponent) {
        items.push({
          name: "createNewTemplateFromHere",
          label: "Create New Template From Here",
          actionType: MenuConstants.ACTION_TYPE_BUTTON_CLICK,
          actionValue: "createNewTemplateFromHere",
        });
      }

      if (this.canCreateCustomComponent) {
        items.push(customComponentItem);
      }

      if (this.canCopy && !this.isFirefox) {
        items.push(copyItem);
      }

      if (this.canPaste && !this.isFirefox) {
        items.push(pasteItem);
      }

      if (this.isDuplicatable) {
        items.push(duplicateItem);
      }

      if (this.isDeletable) {
        items.push(deleteItem);
      }

      return items;
    },
    handleAction(action) {
      switch (action) {
        case "copy":
          this.$emit("copy");
          break;
        case "paste":
          this.$emit("paste");
          break;
        case "edit":
          this.$emit("edit");
          break;
        case "delete":
          this.$emit("delete");
          break;
        case "duplicate":
          this.$emit("duplicate");
          break;
        case "wrapInVerticalContainer":
          this.$emit("wrap-in-vertical-container");
          break;
        case "wrapInHorizontalContainer":
          this.$emit("wrap-in-horizontal-container");
          break;
        case "wrapSelectionInHorizontalContainer":
          this.$emit("wrap-selection-in-horizontal-container");
          break;
        case "wrapSelectionInVerticalContainer":
          this.$emit("wrap-selection-in-vertical-container");
          break;
        case "createCustomComponent":
          this.$emit("create-custom-component");
          break;
        case "detach":
          this.$emit("detach");
          break;
        case "createNewTemplateFromHere":
          this.$emit("create-new-template-from-here");
      }
      if (action === "wrap") {
        this.showSubMenu = this.showSubMenu === "wrap" ? "" : "wrap";
      } else {
        this.showSubMenu = "";
        this.setShowContextualMenuForIdInBuilder(null);
        this.setShowContextualMenuForIdInLayers(null);
      }
    },
    beforeUnmount() {
      this.dropdownItems = [];
    },
  },
  styleGuide: () => ({
    backgroundColor: { "color.background": "paper" },
    borderColor: { "color.border": "dark" },
    borderWidth: { "size.border": "thin" },
    borderRadius: { "size.borderRadius": "large" },

    gap: { "size.spacing": "standard" },

    marginTop: { "size.spacing": "small" },
    marginBottom: { "size.spacing": "small" },
    marginRight: { "size.spacing": "small" },
    marginLeft: { "size.spacing": "small" },

    menuItemFontSize: { "size.font": "small" },
    menuItemFontColor: { "color.font": "standard" },
    menuItemFontWeight: { "font.weight": "standard" },
    menuItemHoverBackgroundColor: { "color.background": "subtle" },
    menuItemPaddingTop: { "size.spacing": "none" },
    menuItemPaddingBottom: { "size.spacing": "none" },
    menuItemPaddingRight: { "size.spacing": "none" },
    menuItemPaddingLeft: { "size.spacing": "none" },

    menuPadding: { "size.spacing": "standard" },
  }),
};
</script>

<style lang="scss" scoped>
.dynamic-component-contextual-menu.col {
  border: 1px solid v-bind('$getStyles.borderColor') !important;
  border-radius: v-bind('$getStyles.borderRadius') !important;
  background-color: v-bind('$getStyles.backgroundColor') !important;
  z-index: 10;
  padding: v-bind("$getStyles.menuPadding") !important;
}

:deep(.submenu.menu) {
  border: 1px solid v-bind('$getStyles.borderColor') !important;
  border-radius: v-bind('$getStyles.borderRadius') !important;
  background-color: v-bind('$getStyles.backgroundColor') !important;
  z-index: 10;
}

:deep(.menu-item) {
  width: 100%;
  border-radius: v-bind('$getStyles.borderRadius') !important;
  padding-top: v-bind('$getStyles.menuItemPaddingTop');
  padding-bottom: v-bind('$getStyles.menuItemPaddingBottom');
  padding-right: v-bind('$getStyles.menuItemPaddingRight');
  padding-left: v-bind('$getStyles.menuItemPaddingLeft');

  &:hover {
    cursor: pointer;
    background-color: v-bind('$getStyles.menuItemHoverBackgroundColor');
  }
}
</style>
